import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from '../lib/api';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import AdminHeader from '../components/AdminHeader';
import {
  DollarSign,
  Key,
  Users,
  ShoppingCart,
  TrendingUp
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if user is authenticated and is admin
    if (!auth.isAuthenticated()) {
      navigate('/login');
      return;
    }

    const userData = auth.getUser();
    if (!userData?.is_admin) {
      navigate('/dashboard');
      return;
    }

    setUser(userData);
    loadAnalytics();
  }, [navigate]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');

      const token = auth.getToken();
      const response = await axios.get(`${API_BASE_URL}/api/admin/analytics/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setStats(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
        <AdminHeader />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-6">
            <Skeleton className="h-12 w-64 bg-gray-800/50" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <Skeleton key={i} className="h-32 bg-gray-800/50" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Admin Header */}
      <AdminHeader />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Revenue Stats */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">Revenue</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-300">Total Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">
                  ${(stats?.total_revenue || 0).toFixed(2)}
                </div>
                <p className="text-xs text-gray-400">All time</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-300">Today</CardTitle>
                <TrendingUp className="h-4 w-4 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">
                  ${(stats?.revenue_today || 0).toFixed(2)}
                </div>
                <p className="text-xs text-gray-400">Revenue today</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-300">This Week</CardTitle>
                <TrendingUp className="h-4 w-4 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">
                  ${(stats?.revenue_this_week || 0).toFixed(2)}
                </div>
                <p className="text-xs text-gray-400">Revenue this week</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-300">This Month</CardTitle>
                <TrendingUp className="h-4 w-4 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">
                  ${(stats?.revenue_this_month || 0).toFixed(2)}
                </div>
                <p className="text-xs text-gray-400">Revenue this month</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* General Stats */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-300">Total Users</CardTitle>
                <Users className="h-4 w-4 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{stats?.total_users || 0}</div>
                <p className="text-xs text-gray-400">Registered users</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-300">Total Licenses</CardTitle>
                <Key className="h-4 w-4 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{stats?.total_licenses || 0}</div>
                <p className="text-xs text-gray-400">
                  {stats?.active_licenses || 0} active
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-300">Total Purchases</CardTitle>
                <ShoppingCart className="h-4 w-4 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">{stats?.total_purchases || 0}</div>
                <p className="text-xs text-gray-400">All time</p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-300">Avg. Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-purple-400" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-white">
                  ${stats?.total_purchases > 0
                    ? ((stats?.total_revenue || 0) / stats.total_purchases).toFixed(2)
                    : '0.00'
                  }
                </div>
                <p className="text-xs text-gray-400">Per purchase</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="text-lg text-white">License Management</CardTitle>
              <CardDescription className="text-gray-400">
                View, create, and manage all licenses
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                <Link to="/admin/licenses">Manage Licenses</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="text-lg text-white">User Management</CardTitle>
              <CardDescription className="text-gray-400">
                View and manage all users
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                <Link to="/admin/users">Manage Users</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="text-lg text-white">Email Management</CardTitle>
              <CardDescription className="text-gray-400">
                Manage inbound emails with AI processing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                <Link to="/admin/emails">Manage Emails</Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
            <CardHeader>
              <CardTitle className="text-lg text-white">Create License</CardTitle>
              <CardDescription className="text-gray-400">
                Manually create a new license
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                <Link to="/admin/licenses/create">Create License</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

